import { useMemo } from 'react';
import {
  AdjustmentMode,
  OwnershipSplitType,
} from '@app/shared/models/contracts/enums/shared-enums';
import { InputModeState, TableSection } from '../types';

export const useTableCellState = (
  tableSection: TableSection,
  renderingSplitType: OwnershipSplitType,
  isProRata: boolean,
  proRataAmountMode: AdjustmentMode,
  proRataSharesMode: AdjustmentMode,
  manualAmountMode: AdjustmentMode,
  manualSharesMode: AdjustmentMode
) => {
  const isClosingBalance = useMemo(
    () =>
      tableSection === TableSection.AmountClosingBalance ||
      tableSection === TableSection.SharesClosingBalance,
    [tableSection]
  );

  const isAmountField = useMemo(
    () =>
      tableSection === TableSection.AmountAdjustment ||
      tableSection === TableSection.AmountClosingBalance ||
      tableSection === TableSection.RealBuybackValue,
    [tableSection]
  );

  const currentAmountMode = isProRata ? proRataAmountMode : manualAmountMode;
  const currentSharesMode = isProRata ? proRataSharesMode : manualSharesMode;

  const inputModeState = useMemo((): InputModeState => {
    return {
      hasAdjustmentInput:
        currentAmountMode === AdjustmentMode.Relative ||
        currentSharesMode === AdjustmentMode.Relative,
      hasClosingInput:
        currentAmountMode === AdjustmentMode.Absolute ||
        currentSharesMode === AdjustmentMode.Absolute,
      amountMode: currentAmountMode,
      sharesMode: currentSharesMode,
    };
  }, [currentAmountMode, currentSharesMode]);

  const shouldBeItalic = useMemo(() => {
    const isEditableContext =
      (renderingSplitType === OwnershipSplitType.ProRata && isProRata) ||
      (renderingSplitType === OwnershipSplitType.Manual && !isProRata);

    if (!isEditableContext) {
      return false;
    }

    // For editable fields, italic indicates active input mode
    switch (tableSection) {
      case TableSection.AmountAdjustment:
        return currentAmountMode === AdjustmentMode.Relative;
      case TableSection.SharesAdjustment:
        return currentSharesMode === AdjustmentMode.Relative;
      case TableSection.AmountClosingBalance:
        return currentAmountMode === AdjustmentMode.Absolute;
      case TableSection.SharesClosingBalance:
        return currentSharesMode === AdjustmentMode.Absolute;
      default:
        return false;
    }
  }, [renderingSplitType, isProRata, tableSection, currentAmountMode, currentSharesMode]);

  return {
    isClosingBalance,
    isAmountField,
    inputModeState,
    shouldBeItalic,
  };
};
